import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { getFloorPlan, saveFloorPlan, updateRegistrationBoothNumber } from '../../../../services/FloorPlanService';
import { isDemoMode } from '../../../../demo/isDemoMode';

// ─── Constants ────────────────────────────────────────────────────────────────

const CELL_SIZE = 52;

const CATEGORY_COLORS = {
  'Jewelry & Accessories': { bg: '#FDF2F8', border: '#EC4899', text: '#9D174D' },
  'Catering':              { bg: '#FFF7ED', border: '#F97316', text: '#9A3412' },
  'Food & Beverage':       { bg: '#FFF1F2', border: '#F43F5E', text: '#881337' },
  'Flowers & Plants':      { bg: '#F0FDF4', border: '#22C55E', text: '#14532D' },
  'Bath & Body':           { bg: '#F5F3FF', border: '#A855F7', text: '#581C87' },
  'Handmade Goods':        { bg: '#FEF3C7', border: '#F59E0B', text: '#78350F' },
  'Arts & Crafts':         { bg: '#EFF6FF', border: '#3B82F6', text: '#1E3A8A' },
  'Art & Prints':          { bg: '#ECFEFF', border: '#06B6D4', text: '#164E63' },
  'default':               { bg: '#F8FAFC', border: '#94A3B8', text: '#475569' },
};

const PAYMENT_COLORS = {
  paid:       { bg: '#F0FDF4', border: '#22C55E', text: '#14532D' },
  'Not Paid': { bg: '#FFF7ED', border: '#F97316', text: '#9A3412' },
  unassigned: { bg: '#F8FAFC', border: '#CBD5E1', text: '#94A3B8' },
};

const getCategoryColor = (cat) => CATEGORY_COLORS[cat] || CATEGORY_COLORS['default'];
const getPaymentColor  = (ps)  => PAYMENT_COLORS[ps]  || PAYMENT_COLORS['unassigned'];

// ─── How-to steps shown in sidebar when nothing is selected ───────────────────

const HOW_TO_STEPS = [
  { num: '1', title: 'Upload your venue map', desc: 'Add a photo or diagram of your space as a background reference.' },
  { num: '2', title: 'Draw booths', desc: 'Switch to Draw Booth, then click and drag to create any size rectangle.' },
  { num: '3', title: 'Mark walkways', desc: 'Use Walkway mode to paint aisle rows so vendors can navigate.' },
  { num: '4', title: 'Assign vendors', desc: 'Click any booth in Select mode to assign an approved vendor.' },
  { num: '5', title: 'Save', desc: 'Hit Save Layout — booth numbers sync automatically to vendor registrations.' },
];

// ─── BoothDetailPanel ─────────────────────────────────────────────────────────

const BoothDetailPanel = ({
  booth, filteredVendors, assignedVendorMap,
  vendorSearch, setVendorSearch, onAssign, onUnassign, onDelete,
}) => (
  <div className="bg-white rounded-xl border border-grey-200 overflow-hidden">
    <div className="px-4 pt-4 pb-3 border-b border-grey-100 flex items-center justify-between">
      <div>
        <p className="text-sm font-semibold text-grey-900">Booth #{booth.number}</p>
        <p className="text-xs text-grey-400">{booth.colSpan} wide × {booth.rowSpan} tall</p>
      </div>
      <button
        onClick={onDelete}
        className="p-1.5 rounded-lg text-grey-400 hover:text-red-600 hover:bg-red-50 transition-colors"
        title="Delete booth"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    </div>

    {/* Current assignment */}
    {booth.vendorId ? (
      <div className="px-4 py-3 bg-grey-50 border-b border-grey-100">
        <p className="text-xs font-semibold text-grey-400 uppercase tracking-wide mb-1">Assigned Vendor</p>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm font-semibold text-grey-900 truncate">{booth.vendorName}</p>
            {booth.category && <p className="text-xs text-grey-500">{booth.category}</p>}
            <span className={`inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-xs font-semibold ${
              booth.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
            }`}>
              {booth.paymentStatus === 'paid' ? 'Paid' : 'Not Paid'}
            </span>
          </div>
          <button
            onClick={onUnassign}
            className="flex-shrink-0 px-2 py-1 text-xs text-grey-500 hover:text-red-600 border border-grey-200 hover:border-red-200 rounded-lg transition-colors"
          >
            Remove
          </button>
        </div>
      </div>
    ) : (
      <div className="px-4 py-2.5 bg-amber-50 border-b border-amber-100">
        <p className="text-xs text-amber-700 font-medium">No vendor assigned — pick one below</p>
      </div>
    )}

    {/* Vendor search */}
    <div className="px-4 py-3">
      <input
        type="text"
        value={vendorSearch}
        onChange={e => setVendorSearch(e.target.value)}
        placeholder="Search approved vendors…"
        className="w-full px-3 py-1.5 text-xs border border-grey-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-grey-300 mb-2"
      />
      <div className="space-y-1 max-h-44 overflow-y-auto">
        {filteredVendors.length === 0 && (
          <p className="text-xs text-grey-400 py-3 text-center">No matching vendors</p>
        )}
        {filteredVendors.map(vendor => {
          const alreadyOn = assignedVendorMap[vendor.id];
          const isCurrent = booth.vendorId === vendor.id;
          return (
            <button
              key={vendor.id}
              onClick={() => !isCurrent && onAssign(vendor)}
              className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors ${
                isCurrent
                  ? 'bg-grey-900 text-white cursor-default'
                  : alreadyOn
                  ? 'bg-grey-50 text-grey-400 hover:bg-grey-100'
                  : 'hover:bg-primary-50 text-grey-800'
              }`}
            >
              <div className="min-w-0">
                <p className="text-xs font-semibold truncate">{vendor.vendorName}</p>
                <p className={`text-xs ${isCurrent ? 'text-grey-300' : 'text-grey-400'}`}>{vendor.category}</p>
              </div>
              {alreadyOn && !isCurrent && (
                <span className="text-xs text-grey-400 ml-2 flex-shrink-0">Booth #{alreadyOn}</span>
              )}
              {isCurrent && <span className="text-xs text-grey-300 ml-2">current</span>}
            </button>
          );
        })}
      </div>
    </div>
  </div>
);

// ─── Legend ───────────────────────────────────────────────────────────────────

const Legend = ({ viewMode }) => {
  const items = viewMode === 'payment'
    ? [
        { label: 'Paid',       c: PAYMENT_COLORS.paid },
        { label: 'Not Paid',   c: PAYMENT_COLORS['Not Paid'] },
        { label: 'Unassigned', c: PAYMENT_COLORS.unassigned },
      ]
    : Object.entries(CATEGORY_COLORS)
        .filter(([k]) => k !== 'default')
        .slice(0, 5)
        .map(([label, c]) => ({ label, c }));

  return (
    <div className="bg-white rounded-xl border border-grey-200 px-4 py-3">
      <p className="text-xs font-semibold text-grey-400 uppercase tracking-wide mb-2">Legend</p>
      <div className="space-y-1.5">
        {items.map(({ label, c }) => (
          <div key={label} className="flex items-center gap-2">
            <div className="w-3.5 h-3.5 rounded-sm border-2 flex-shrink-0"
              style={{ backgroundColor: c.bg, borderColor: c.border }} />
            <span className="text-xs text-grey-600">{label}</span>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <div className="w-3.5 h-3.5 rounded-sm bg-grey-100 border border-grey-300 flex-shrink-0" />
          <span className="text-xs text-grey-600">Walkway / Aisle</span>
        </div>
      </div>
    </div>
  );
};

// ─── VenueImageUpload ─────────────────────────────────────────────────────────

const VenueImageUpload = ({ imageUrl, imageOpacity, onImageChange, onOpacityChange }) => {
  const inputRef = useRef(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => onImageChange(ev.target.result);
    reader.readAsDataURL(file);
  };

  return (
    <div className="bg-white rounded-xl border border-grey-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-grey-100">
        <p className="text-sm font-semibold text-grey-900">Venue Map</p>
        <p className="text-xs text-grey-400 mt-0.5">
          Upload your venue floor plan as a reference layer behind the grid
        </p>
      </div>
      <div className="px-4 py-3 space-y-3">
        {imageUrl ? (
          <div className="space-y-2">
            <img
              src={imageUrl}
              alt="Venue map preview"
              className="w-full h-20 object-cover rounded-lg border border-grey-200"
            />
            <div>
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs text-grey-500 font-medium">Opacity</p>
                <p className="text-xs text-grey-400">{Math.round(imageOpacity * 100)}%</p>
              </div>
              <input
                type="range"
                min={0.1} max={1} step={0.05}
                value={imageOpacity}
                onChange={e => onOpacityChange(parseFloat(e.target.value))}
                className="w-full h-1.5 accent-grey-900"
              />
            </div>
            <button
              onClick={() => onImageChange(null)}
              className="w-full text-xs text-red-500 hover:text-red-700 py-1.5 border border-red-100 hover:border-red-200 rounded-lg transition-colors"
            >
              Remove image
            </button>
          </div>
        ) : (
          <button
            onClick={() => inputRef.current?.click()}
            className="w-full flex flex-col items-center gap-2 py-5 border-2 border-dashed border-grey-200 rounded-xl hover:border-grey-400 hover:bg-grey-50 transition-colors"
          >
            <svg className="w-6 h-6 text-grey-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            <span className="text-xs font-medium text-grey-500">Upload venue map or photo</span>
            <span className="text-xs text-grey-400">PNG, JPG, PDF screenshot</span>
          </button>
        )}
        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
    </div>
  );
};

// ─── GridSizeControls ─────────────────────────────────────────────────────────

const GridSizeControls = ({ width, height, onChange }) => (
  <div className="bg-white rounded-xl border border-grey-200 px-4 py-3">
    <p className="text-xs font-semibold text-grey-400 uppercase tracking-wide mb-2">Grid Size</p>
    <div className="grid grid-cols-2 gap-2">
      {[
        { label: 'Columns', key: 'width',  val: width,  min: 4, max: 30 },
        { label: 'Rows',    key: 'height', val: height, min: 3, max: 20 },
      ].map(({ label, key, val, min, max }) => (
        <div key={key}>
          <p className="text-xs text-grey-500 mb-1">{label}</p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => onChange(key, Math.max(min, val - 1))}
              className="w-7 h-7 flex items-center justify-center rounded-lg border border-grey-200 text-grey-600 hover:bg-grey-100 text-sm font-bold"
            >−</button>
            <span className="flex-1 text-center text-sm font-semibold text-grey-900">{val}</span>
            <button
              onClick={() => onChange(key, Math.min(max, val + 1))}
              className="w-7 h-7 flex items-center justify-center rounded-lg border border-grey-200 text-grey-600 hover:bg-grey-100 text-sm font-bold"
            >+</button>
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ─── FloorPlanEditor ──────────────────────────────────────────────────────────

const FloorPlanEditor = ({ eventId, registrations = [] }) => {
  const [plan, setPlan] = useState({ gridWidth: 12, gridHeight: 8, booths: [], pathwayCells: [] });
  const [venueImageUrl, setVenueImageUrl]     = useState(null);
  const [imageOpacity, setImageOpacity]       = useState(0.35);
  const [mode, setMode]     = useState('draw');
  const [viewMode, setViewMode] = useState('category');
  const [dragStart, setDragStart]     = useState(null);
  const [dragCurrent, setDragCurrent] = useState(null);
  const [isMouseDown, setIsMouseDown] = useState(false);
  const [selectedBoothId, setSelectedBoothId] = useState(null);
  const [isSaving, setIsSaving]   = useState(false);
  const [isDirty, setIsDirty]     = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [nextBoothNum, setNextBoothNum] = useState(1);
  const [vendorSearch, setVendorSearch] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  // Load
  useEffect(() => {
    if (!eventId) return;
    (async () => {
      setIsLoading(true);
      try {
        const data = await getFloorPlan(eventId);
        if (data) {
          setPlan({
            gridWidth:    data.gridWidth    || 12,
            gridHeight:   data.gridHeight   || 8,
            booths:       data.booths       || [],
            pathwayCells: data.pathwayCells || [],
          });
          if (data.venueImageUrl) setVenueImageUrl(data.venueImageUrl);
          if (data.imageOpacity)  setImageOpacity(data.imageOpacity);
          const maxN = (data.booths || []).reduce((m, b) => {
            const n = parseInt(b.number, 10); return isNaN(n) ? m : Math.max(m, n);
          }, 0);
          setNextBoothNum(maxN + 1);
          // Default to select if booths exist, draw if empty
          setMode((data.booths || []).length > 0 ? 'select' : 'draw');
        }
      } catch (e) { console.error(e); }
      finally { setIsLoading(false); }
    })();
  }, [eventId]);

  // Cell occupancy map
  const cellMap = useMemo(() => {
    const map = {};
    for (const b of plan.booths) {
      for (let r = b.row; r < b.row + b.rowSpan; r++)
        for (let c = b.col; c < b.col + b.colSpan; c++)
          map[`${r},${c}`] = { type: 'booth', booth: b, isOrigin: r === b.row && c === b.col };
    }
    for (const key of plan.pathwayCells) if (!map[key]) map[key] = { type: 'pathway' };
    return map;
  }, [plan]);

  // Draw preview
  const drawRect = useMemo(() => {
    if (mode !== 'draw' || !isMouseDown || !dragStart || !dragCurrent) return null;
    return {
      row:     Math.min(dragStart.r, dragCurrent.r),
      col:     Math.min(dragStart.c, dragCurrent.c),
      rowSpan: Math.abs(dragCurrent.r - dragStart.r) + 1,
      colSpan: Math.abs(dragCurrent.c - dragStart.c) + 1,
    };
  }, [mode, isMouseDown, dragStart, dragCurrent]);

  const drawPreviewCells = useMemo(() => {
    if (!drawRect) return new Set();
    const s = new Set();
    for (let r = drawRect.row; r < drawRect.row + drawRect.rowSpan; r++)
      for (let c = drawRect.col; c < drawRect.col + drawRect.colSpan; c++)
        s.add(`${r},${c}`);
    return s;
  }, [drawRect]);

  const drawHasOverlap = useMemo(() =>
    [...drawPreviewCells].some(k => cellMap[k]?.type === 'booth'),
    [drawPreviewCells, cellMap]
  );

  // Mouse up — finalize draw
  const handleMouseUp = useCallback(() => {
    if (mode === 'draw' && isMouseDown && drawRect && !drawHasOverlap) {
      const newBooth = {
        id: `booth-${Date.now()}`,
        number: String(nextBoothNum),
        row: drawRect.row, col: drawRect.col,
        rowSpan: drawRect.rowSpan, colSpan: drawRect.colSpan,
        vendorId: null, vendorName: null, category: null, paymentStatus: null,
      };
      setPlan(prev => ({ ...prev, booths: [...prev.booths, newBooth] }));
      setNextBoothNum(n => n + 1);
      setIsDirty(true);
      setSelectedBoothId(newBooth.id);
      setMode('select');
      setVendorSearch('');
    }
    setIsMouseDown(false);
    setDragStart(null);
    setDragCurrent(null);
  }, [mode, isMouseDown, drawRect, drawHasOverlap, nextBoothNum]);

  useEffect(() => {
    window.addEventListener('mouseup', handleMouseUp);
    return () => window.removeEventListener('mouseup', handleMouseUp);
  }, [handleMouseUp]);

  const handleCellMouseDown = (r, c, e) => {
    e.preventDefault();
    if (mode === 'draw') {
      setDragStart({ r, c }); setDragCurrent({ r, c }); setIsMouseDown(true);
    } else if (mode === 'select') {
      const cell = cellMap[`${r},${c}`];
      if (cell?.type === 'booth') { setSelectedBoothId(cell.booth.id); setVendorSearch(''); }
      else setSelectedBoothId(null);
    } else if (mode === 'pathway') {
      togglePathway(r, c); setIsMouseDown(true);
    }
  };

  const handleCellMouseEnter = (r, c) => {
    if (mode === 'draw' && isMouseDown) setDragCurrent({ r, c });
    else if (mode === 'pathway' && isMouseDown) togglePathway(r, c, true);
  };

  const togglePathway = (r, c, forceAdd) => {
    const key = `${r},${c}`;
    setPlan(prev => {
      const s = new Set(prev.pathwayCells);
      if (forceAdd === true)  s.add(key);
      else if (s.has(key))    s.delete(key);
      else                    s.add(key);
      return { ...prev, pathwayCells: [...s] };
    });
    setIsDirty(true);
  };

  const deleteBooth = (id) => {
    setPlan(p => ({ ...p, booths: p.booths.filter(b => b.id !== id) }));
    if (selectedBoothId === id) setSelectedBoothId(null);
    setIsDirty(true);
  };

  const assignVendor = (vendor) => {
    setPlan(prev => ({
      ...prev,
      booths: prev.booths.map(b => {
        if (b.vendorId === vendor.id) return { ...b, vendorId: null, vendorName: null, category: null, paymentStatus: null };
        if (b.id === selectedBoothId) return { ...b, vendorId: vendor.id, vendorName: vendor.vendorName, category: vendor.category, paymentStatus: vendor.paymentStatus };
        return b;
      }),
    }));
    setIsDirty(true);
    setVendorSearch('');
  };

  const unassignVendor = () => {
    setPlan(prev => ({
      ...prev,
      booths: prev.booths.map(b =>
        b.id === selectedBoothId ? { ...b, vendorId: null, vendorName: null, category: null, paymentStatus: null } : b
      ),
    }));
    setIsDirty(true);
  };

  const handleGridSizeChange = (key, val) => {
    setPlan(prev => ({ ...prev, [key === 'width' ? 'gridWidth' : 'gridHeight']: val }));
    setIsDirty(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await saveFloorPlan(eventId, {
        gridWidth:    plan.gridWidth,
        gridHeight:   plan.gridHeight,
        booths:       plan.booths,
        pathwayCells: plan.pathwayCells,
        venueImageUrl: isDemoMode ? null : venueImageUrl, // don't persist base64 in demo
        imageOpacity,
      });
      for (const booth of plan.booths) {
        if (booth.vendorId) {
          const reg = registrations.find(r => (r.userId || r.mpsUserId) === booth.vendorId);
          if (reg?.id) await updateRegistrationBoothNumber(reg.id, booth.number);
        }
      }
      setIsDirty(false);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setIsSaving(false);
    }
  };

  // Derived
  const selectedBooth = plan.booths.find(b => b.id === selectedBoothId);

  const approvedVendors = useMemo(() =>
    registrations
      .filter(r => r.applicationStatus === 'Approved' || r.applicationStatus === 'Registered')
      .map(r => ({
        id: r.userId || r.mpsUserId,
        regId: r.id,
        vendorName: r.businessName || r.vendorName,
        category: r.category,
        paymentStatus: r.paymentStatus,
      })),
    [registrations]
  );

  const assignedVendorMap = useMemo(() => {
    const m = {};
    for (const b of plan.booths) if (b.vendorId) m[b.vendorId] = b.number;
    return m;
  }, [plan.booths]);

  const filteredVendors = useMemo(() =>
    approvedVendors.filter(v => v.vendorName?.toLowerCase().includes(vendorSearch.toLowerCase())),
    [approvedVendors, vendorSearch]
  );

  const stats = {
    total:    plan.booths.length,
    assigned: plan.booths.filter(b => b.vendorId).length,
    empty:    plan.booths.filter(b => !b.vendorId).length,
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="w-full px-8 py-16 flex items-center justify-center">
        <p className="text-sm text-grey-400">Loading floor plan…</p>
      </div>
    );
  }

  const tools = [
    {
      id: 'select',
      label: 'Select & Assign',
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5" />
        </svg>
      ),
      hint: 'Click any booth to assign vendors or delete it',
    },
    {
      id: 'draw',
      label: 'Draw Booth',
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      ),
      hint: 'Click and drag on the grid to draw a booth',
    },
    {
      id: 'pathway',
      label: 'Mark Walkway',
      icon: (
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      ),
      hint: 'Click or drag cells to mark aisles and walkways',
    },
  ];

  const activeTool = tools.find(t => t.id === mode);

  return (
    <div className="w-full px-4 sm:px-8 py-6">

      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-5">
        <div className="flex items-start gap-3">
          <div>
            <h2 className="text-lg font-semibold text-grey-900">Floor Plan</h2>
            <p className="text-sm text-grey-400 mt-0.5">
              {stats.total === 0
                ? 'Draw your first booth below'
                : `${stats.total} booth${stats.total !== 1 ? 's' : ''} · ${stats.assigned} assigned · ${stats.empty} empty`}
            </p>
          </div>
          <button
            onClick={() => setShowHelp(h => !h)}
            className="mt-0.5 p-1.5 rounded-lg text-grey-400 hover:text-grey-700 hover:bg-grey-100 transition-colors"
            title="How to use"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>
        <button
          onClick={handleSave}
          disabled={!isDirty || isSaving}
          className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all ${
            isDirty && !isSaving ? 'bg-grey-900 text-white hover:bg-grey-700 shadow-sm' : 'bg-grey-100 text-grey-400 cursor-not-allowed'
          }`}
        >
          {isSaving ? (
            <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/></svg> Saving…</>
          ) : isDirty ? (
            <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg> Save Layout</>
          ) : (
            <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> Saved</>
          )}
        </button>
      </div>

      {/* How-to panel */}
      {showHelp && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl px-5 py-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-blue-900">How to use the Floor Plan</p>
            <button onClick={() => setShowHelp(false)} className="text-blue-400 hover:text-blue-600">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="space-y-2">
            {HOW_TO_STEPS.map(s => (
              <div key={s.num} className="flex items-start gap-3">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-200 text-blue-800 text-xs font-bold flex items-center justify-center">{s.num}</span>
                <div>
                  <span className="text-xs font-semibold text-blue-900">{s.title} — </span>
                  <span className="text-xs text-blue-700">{s.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center bg-grey-100 rounded-xl p-1 gap-0.5">
          {tools.map(tool => (
            <button
              key={tool.id}
              onClick={() => { setMode(tool.id); if (tool.id !== 'select') setSelectedBoothId(null); }}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                mode === tool.id ? 'bg-white text-grey-900 shadow-sm' : 'text-grey-500 hover:text-grey-800'
              }`}
            >
              {tool.icon}
              {tool.label}
            </button>
          ))}
        </div>

        <div className="flex items-center bg-grey-100 rounded-xl p-1 gap-0.5 sm:ml-auto">
          {[{ id: 'category', label: 'By Category' }, { id: 'payment', label: 'By Payment' }].map(vm => (
            <button
              key={vm.id}
              onClick={() => setViewMode(vm.id)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                viewMode === vm.id ? 'bg-white text-grey-900 shadow-sm' : 'text-grey-500 hover:text-grey-800'
              }`}
            >
              {vm.label}
            </button>
          ))}
        </div>
      </div>

      {/* Active hint */}
      <div className="mb-4 flex items-center gap-2 px-3 py-2 bg-grey-50 rounded-xl border border-grey-200">
        <div className="text-grey-400">{activeTool?.icon}</div>
        <p className="text-xs text-grey-500 font-medium">{activeTool?.hint}</p>
        {mode === 'draw' && (
          <span className="ml-auto text-xs text-grey-400">Drag larger for bigger booths (food trucks, etc.)</span>
        )}
      </div>

      {/* Canvas + Sidebar */}
      <div className="flex flex-col lg:flex-row gap-5 items-start">

        {/* Grid */}
        <div className="overflow-auto flex-shrink-0 rounded-xl border border-grey-200 bg-white p-3 shadow-sm">
          <div
            style={{
              position: 'relative',
              width:  plan.gridWidth  * CELL_SIZE,
              height: plan.gridHeight * CELL_SIZE,
            }}
          >
            {/* Venue image background */}
            {venueImageUrl && (
              <img
                src={venueImageUrl}
                alt="Venue map"
                style={{
                  position:   'absolute',
                  inset:      0,
                  width:      '100%',
                  height:     '100%',
                  objectFit:  'cover',
                  opacity:    imageOpacity,
                  pointerEvents: 'none',
                  zIndex:     1,
                  borderRadius: 4,
                }}
              />
            )}

            {/* Grid cells */}
            {Array.from({ length: plan.gridHeight }, (_, r) =>
              Array.from({ length: plan.gridWidth }, (_, c) => {
                const key = `${r},${c}`;
                const cell = cellMap[key];
                const inPreview = drawPreviewCells.has(key);

                let bg = '#FFFFFF', border = '#F0F0F0';
                if (cell?.type === 'pathway') { bg = '#F3F4F6'; border = '#D1D5DB'; }
                if (inPreview) {
                  bg     = drawHasOverlap ? '#FEE2E2' : '#EFF6FF';
                  border = drawHasOverlap ? '#F87171' : '#93C5FD';
                }
                const cursor = mode === 'draw' ? 'crosshair' : mode === 'pathway' ? 'cell' : 'default';
                return (
                  <div
                    key={key}
                    style={{
                      position: 'absolute', left: c * CELL_SIZE, top: r * CELL_SIZE,
                      width: CELL_SIZE, height: CELL_SIZE,
                      background: bg, border: `1px solid ${border}`,
                      cursor, boxSizing: 'border-box', zIndex: 2,
                    }}
                    onMouseDown={e => handleCellMouseDown(r, c, e)}
                    onMouseEnter={() => handleCellMouseEnter(r, c)}
                  />
                );
              })
            )}

            {/* Booths */}
            {plan.booths.map(booth => {
              const isSelected = booth.id === selectedBoothId;
              const colors = viewMode === 'payment'
                ? getPaymentColor(booth.vendorId ? booth.paymentStatus : 'unassigned')
                : getCategoryColor(booth.category);

              return (
                <div
                  key={booth.id}
                  style={{
                    position:  'absolute',
                    left:  booth.col * CELL_SIZE + 3,
                    top:   booth.row * CELL_SIZE + 3,
                    width:  booth.colSpan * CELL_SIZE - 6,
                    height: booth.rowSpan * CELL_SIZE - 6,
                    backgroundColor: colors.bg,
                    border:    `2px solid ${isSelected ? '#111827' : colors.border}`,
                    boxShadow: isSelected ? '0 0 0 3px rgba(17,24,39,0.15)' : '0 1px 3px rgba(0,0,0,0.08)',
                    borderRadius: 7, zIndex: 10, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    padding: '4px 6px', cursor: mode === 'select' ? 'pointer' : 'default',
                    userSelect: 'none', transition: 'box-shadow 0.1s, border-color 0.1s',
                  }}
                  onMouseDown={e => {
                    e.stopPropagation();
                    if (mode === 'select') { setSelectedBoothId(booth.id); setVendorSearch(''); }
                  }}
                >
                  <span style={{ fontSize: 9, fontWeight: 800, color: colors.border, lineHeight: 1, marginBottom: 2 }}>
                    #{booth.number}
                  </span>
                  {booth.vendorName && (
                    <span style={{
                      fontSize: booth.colSpan > 1 ? 10 : 9, fontWeight: 600, color: colors.text,
                      textAlign: 'center', lineHeight: 1.2, maxWidth: '100%', overflow: 'hidden',
                      display: '-webkit-box', WebkitLineClamp: booth.rowSpan > 1 ? 3 : 2, WebkitBoxOrient: 'vertical',
                    }}>
                      {booth.vendorName}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Column numbers */}
          <div style={{ display: 'flex', width: plan.gridWidth * CELL_SIZE, marginTop: 4 }}>
            {Array.from({ length: plan.gridWidth }, (_, i) => (
              <div key={i} style={{ width: CELL_SIZE, textAlign: 'center', fontSize: 9, color: '#9CA3AF', fontWeight: 600 }}>
                {i + 1}
              </div>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div className="flex-1 w-full lg:max-w-xs space-y-3">
          {selectedBooth ? (
            <BoothDetailPanel
              booth={selectedBooth}
              filteredVendors={filteredVendors}
              assignedVendorMap={assignedVendorMap}
              vendorSearch={vendorSearch}
              setVendorSearch={setVendorSearch}
              onAssign={assignVendor}
              onUnassign={unassignVendor}
              onDelete={() => deleteBooth(selectedBooth.id)}
            />
          ) : (
            <div className="bg-white rounded-xl border border-grey-200 px-4 py-5">
              <p className="text-sm font-semibold text-grey-700 mb-3">
                {mode === 'draw' ? 'Drawing mode active' : mode === 'pathway' ? 'Walkway mode active' : 'No booth selected'}
              </p>
              <div className="space-y-2">
                {HOW_TO_STEPS.map(s => (
                  <div key={s.num} className="flex items-start gap-2.5">
                    <span className={`flex-shrink-0 w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${
                      (s.num === '2' && mode === 'draw') || (s.num === '3' && mode === 'pathway')
                        ? 'bg-grey-900 text-white'
                        : 'bg-grey-100 text-grey-400'
                    }`}>{s.num}</span>
                    <div>
                      <p className="text-xs font-semibold text-grey-700">{s.title}</p>
                      <p className="text-xs text-grey-400">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { label: 'Total', value: stats.total },
              { label: 'Assigned', value: stats.assigned, highlight: stats.assigned > 0 },
              { label: 'Empty', value: stats.empty, warn: stats.empty > 0 && stats.total > 0 },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl border border-grey-200 py-3 text-center">
                <p className={`text-xl font-bold ${s.highlight ? 'text-green-600' : s.warn ? 'text-amber-500' : 'text-grey-900'}`}>
                  {s.value}
                </p>
                <p className="text-xs text-grey-400 mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>

          <VenueImageUpload
            imageUrl={venueImageUrl}
            imageOpacity={imageOpacity}
            onImageChange={url => { setVenueImageUrl(url); setIsDirty(true); }}
            onOpacityChange={v => { setImageOpacity(v); setIsDirty(true); }}
          />

          <GridSizeControls
            width={plan.gridWidth}
            height={plan.gridHeight}
            onChange={handleGridSizeChange}
          />

          <Legend viewMode={viewMode} />
        </div>
      </div>
    </div>
  );
};

export default FloorPlanEditor;
